import snapshot from './generated/mobile-content.json';
import type { BaseLanguage, Dialect, Voice } from '@/providers/LearnerPreferences';

export type LearningCard = {
  title: string;
  translation: string;
  translations?: Record<string, unknown>;
  subtitle?: string;
  sound?: string | null;
  soundChildren?: string | null;
  image?: string | null;
  persistencyKey: string;
  cardColor?: string;
};

type UnknownRecord = Record<string, unknown>;
const modules = snapshot.translations as Record<string, UnknownRecord>;
const contentCdnBase = (import.meta.env.VITE_CDN_BASE_URL ?? '').replace(/\/$/, '');

function contentAsset(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  return contentCdnBase && value.startsWith('/') ? `${contentCdnBase}${value}` : value;
}

function arraysIn(value: unknown): Array<{ key: string; rows: UnknownRecord[] }> {
  if (!value || typeof value !== 'object') return [];
  return Object.entries(value as UnknownRecord).flatMap(([key, candidate]) => {
    if (Array.isArray(candidate)) {
      const nestedRows = (candidate as UnknownRecord[]).flatMap((row) =>
        Array.isArray(row.items) ? (row.items as UnknownRecord[]) : [],
      );
      return [{ key, rows: nestedRows.length ? nestedRows : (candidate as UnknownRecord[]) }];
    }
    if (candidate && typeof candidate === 'object') {
      return Object.entries(candidate as UnknownRecord)
        .filter(([, nested]) => Array.isArray(nested))
        .map(([nestedKey, nested]) => ({ key: `${key}_${nestedKey}`, rows: nested as UnknownRecord[] }));
    }
    return [];
  });
}

function localizedTranslation(row: UnknownRecord, language: BaseLanguage): string {
  const translations = row.translations;
  if (translations && typeof translations === 'object') {
    const direct = (translations as UnknownRecord)[language];
    if (typeof direct === 'string') return direct;
    const nested = (translations as UnknownRecord).translation;
    if (nested && typeof nested === 'object') {
      const value = (nested as UnknownRecord)[language];
      if (typeof value === 'string') return value;
    }
    for (const nestedKey of ['used_for', 'examples', 'subtitle']) {
      const nestedValues = (translations as UnknownRecord)[nestedKey];
      if (nestedValues && typeof nestedValues === 'object') {
        const value = (nestedValues as UnknownRecord)[language];
        if (typeof value === 'string') return value;
      }
    }
  }
  for (const key of ['translation', 'english', 'definition', 'meaning', 'used_for']) {
    if (typeof row[key] === 'string') return row[key] as string;
  }
  return '';
}

function normalize(row: UnknownRecord, language: BaseLanguage, index: number): LearningCard | null {
  const title = ['title', 'hmong', 'script', 'word', 'classifier']
    .map((key) => row[key])
    .find((value): value is string => typeof value === 'string');
  if (!title) return null;
  return {
    title,
    translation:
      row.isNumber && typeof row.translation === 'string'
        ? row.translation
        : localizedTranslation(row, language),
    translations: row.translations as Record<string, unknown> | undefined,
    subtitle: typeof row.subtitle === 'string' ? row.subtitle : undefined,
    sound: contentAsset(row.sound),
    soundChildren: contentAsset(row.soundChildren),
    image:
      typeof row.imageSource === 'string'
        ? contentAsset(row.imageSource)
        : typeof row.image === 'string'
          ? contentAsset(row.image)
          : null,
    persistencyKey:
      typeof row.persistencyKey === 'string'
        ? row.persistencyKey
        : `web-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${index}`,
    cardColor: typeof row.cardColor === 'string' ? row.cardColor : undefined,
  };
}

function moduleArrays(slug: string, dialect: Dialect) {
  const moduleName: Record<string, string> = {
    'intro-to-siab': 'siab-intro',
    'intro-to-hmong-tones': 'intro-hmong-tones',
  };
  const module = modules[moduleName[slug] || slug];
  if (!module) return [];
  const arrays = arraysIn(module).filter(({ rows }) => rows.some((row) => row && typeof row === 'object'));
  const preferred = arrays.filter(({ key }) =>
    dialect === 'moob' ? key.toUpperCase().includes('MOOB') : !key.toUpperCase().includes('MOOB'),
  );
  return (preferred.length ? preferred : arrays).filter(({ key }) => !key.toUpperCase().includes('COVER'));
}

export function localCardGroups(
  slug: string,
  dialect: Dialect,
  language: BaseLanguage,
): Array<{ key: string; cards: LearningCard[] }> {
  return moduleArrays(slug, dialect).map(({ key, rows }) => ({
    key,
    cards: rows.map((row, index) => normalize(row, language, index)).filter((row): row is LearningCard => row !== null),
  }));
}

export function localCards(
  slug: string,
  dialect: Dialect,
  language: BaseLanguage,
  options?: { keyIncludes?: string; keyExcludes?: string },
): LearningCard[] {
  const groups = localCardGroups(slug, dialect, language);
  const filtered = groups.filter(({ key }) => {
    const upper = key.toUpperCase();
    if (options?.keyIncludes && !upper.includes(options.keyIncludes.toUpperCase())) return false;
    if (options?.keyExcludes && upper.includes(options.keyExcludes.toUpperCase())) return false;
    return true;
  });
  const selected = (filtered.length ? filtered : groups).sort((a, b) => b.cards.length - a.cards.length)[0];
  return selected?.cards ?? [];
}

export function allLocalFlashcards(dialect: Dialect, language: BaseLanguage): LearningCard[] {
  const slugs = [
    'numbers', 'colors', 'pronouns', 'family', 'verbs', 'animals', 'food', 'places',
    'clothing', 'taste', 'face-parts', 'internal-organs', 'fathers-side', 'mothers-side',
    'fathers-day', 'mothers-day', 'december', 'halloween', 'thanksgiving',
  ];
  const seen = new Set<string>();
  const cards: LearningCard[] = [];
  for (const slug of slugs) {
    for (const card of localCards(slug, dialect, language)) {
      if (seen.has(card.persistencyKey)) continue;
      seen.add(card.persistencyKey);
      cards.push(card);
    }
  }
  return cards;
}

export function cardAudio(card: LearningCard, voice: Voice) {
  return voice === 'adult' ? card.sound : card.soundChildren || card.sound;
}

type LocalStoryPage = {
  image?: string | null;
  sound?: string | null;
  soundChildren?: string | null;
  background?: string;
  copy?: string;
};

export const localStorybooks = Object.fromEntries(
  Object.entries(snapshot.storybooks).map(([key, pages]) => [
    key,
    pages.map((page) => ({
      ...page,
      image: contentAsset('image' in page ? page.image : null),
      sound: contentAsset(page.sound),
      soundChildren: contentAsset(page.soundChildren),
    })),
  ]),
) as unknown as Record<keyof typeof snapshot.storybooks, LocalStoryPage[]>;
export const localSongs = Object.fromEntries(
  Object.entries(snapshot.songs).map(([key, song]) => [
    key,
    { ...song, audio: contentAsset(song.audio) ?? song.audio },
  ]),
) as typeof snapshot.songs;

export type AlphabetLetter = {
  script: string;
  pronunciation: string;
  sound?: string | null;
  soundChildren?: string | null;
};

export function localAlphabet(dialect: Dialect): Record<string, AlphabetLetter[]> {
  const alphabetModule = modules.alphabet as UnknownRecord;
  const value = alphabetModule[
    dialect === 'moob' ? 'MOOB_ALPHABET' : 'HMONG_ALPHABET'
  ] as UnknownRecord | undefined;
  if (!value) return {};
  const groups: Record<string, AlphabetLetter[]> = {};
  for (const [family, familyValue] of Object.entries(value)) {
    if (Array.isArray(familyValue)) {
      groups[family] = familyValue as AlphabetLetter[];
      continue;
    }
    if (familyValue && typeof familyValue === 'object') {
      for (const [name, entries] of Object.entries(familyValue as UnknownRecord)) {
        if (Array.isArray(entries)) groups[`${family} · ${name}`] = entries as AlphabetLetter[];
      }
    }
  }
  return Object.fromEntries(
    Object.entries(groups).map(([name, entries]) => [
      name,
      entries.map((entry) => ({
        ...entry,
        sound: contentAsset(entry.sound),
        soundChildren: contentAsset(entry.soundChildren),
      })),
    ]),
  );
}
